import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/system';
import { StepButton, StepLabel, StepIcon, Button, useMediaQuery } from '@mui/material';
import { CLEAR_SHIPMENT } from '#action/types';
import PreloaderComponent from '#component/preloader';
import { fetchShipmentDeliveryCargoFiles } from '#action/shipment/shipment-delivery-cargo';
import { clearProjectAlerts } from '#action/project/alerts';
import { fetchShipmentDate } from '#action/shipment/dashboard';
import { fetchShipmentContainers, fetchShipmentGoods } from '#action/shipment/cargo';
import { clearCopiedShipment } from '#action/shipment/copied-shipment';
import { fetchAddressbooks, clear as clearAddressbooks } from '#action/addressbook/addressbooks';
import { updateBreadcrumb } from '#action/breadcrumb';
import { formatter, getShipmentStatus } from '#component/shipment/helper';
import { resetFormData, setAfterSubmitAction, goToStep, fetchNotificationContacts, setJustSelectedTab } from '#action/shipment/form';
import { fetchShipmentNotificationRecipients } from '#action/shipment/recipients';
import { setShipmentData } from '#action/shipment/overview';
import { clearProjectCustomFields } from '#action/project/custom-fields';
import CustomStepIcon from './custom-step-icon';
import SaveButton from './save-button';
import Documents from './documents';
import Notification from './notification';
import Cargo from './cargo';
import { Cnt, StyledStepper, StyledStep } from './style';
import Delivery from './delivery';
import EditShipment from './shipment';
import { setDocumentTitle } from '#helper';

const steps = [
  { label: 'Shipment', mobileLabel: 'Shipment', form: 'EditShipmentForm' },
  { label: 'Lane', mobileLabel: 'Lane', form: 'ShipmentDeliveryForm' },
  { label: 'Cargo', mobileLabel: 'Cargo', form: 'ShipmentCargoForm' },
  { label: 'Notification', mobileLabel: 'Notification', form: 'ShipmentNotificationForm' },
  { label: 'Documents', mobileLabel: 'Docs', form: 'ShipmentDocumentsForm' }
];

const CancelButtonComponent = props => {
  const history = useHistory();
  const location = useLocation();

  const handleBackClick = () => {
    if (location.key) {
      history.goBack();
    } else {
      history.push('/shipment');
    }
  };

  return (
    <Button variant="outlined" {...props} onClick={handleBackClick}>
      Cancel
    </Button>
  );
};

const Shipment = props => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    match: { params: { id } },
    history
  } = props;

  const dispatch = useDispatch();
  const [onInit, setInit] = useState(false);
  const step = useSelector(s => s.shipmentForm.step);
  const shipment = useSelector(s => s.shipment);
  const copiedShipment = useSelector(s => s.copiedShipment);

  const isCopiedShipment = history.location.search.includes('copied');

  const pristine = useSelector(s => s.shipmentForm.pristine);
  const isAnyChanged = Object.values(pristine).some(v => !v);
  const editable = isCopiedShipment ? true : getShipmentStatus(shipment)?.code < 3;
  const formStatus = useSelector(s => s.shipmentForm.status);

  useEffect(() => {
    if (id) {
      setDocumentTitle('Shipment details', shipment.label || formatter(shipment.uid));
    } else {
      setDocumentTitle('Add shipment');
    }
  }, [id, shipment.label, shipment.uid]);

  useEffect(() => {
    if (id) {
      if (shipment.uid) {
        dispatch(updateBreadcrumb([
          { label: 'Shipments', path: '/shipment' },
          { label: formatter(shipment.uid), path: `/shipment/${id}` },
          { label: 'Shipment details' }
        ]));
      }
    } else {
      dispatch(updateBreadcrumb([
        { label: 'Shipments', path: '/shipment' },
        { label: 'Create shipment' }
      ]));
    }
  }, [dispatch, shipment.uid, id]);

  useEffect(() => {
    const fetchDataForAdd = async () => {
      await Promise.all([
        dispatch(fetchShipmentDate()),
        dispatch(fetchShipmentGoods()),
        dispatch(fetchShipmentContainers()),
        dispatch(fetchNotificationContacts())
      ]);
      setInit(true);
    };
    const fetchDataForEdit = async () => {
      await Promise.all([
        dispatch(fetchNotificationContacts()),
        dispatch(fetchShipmentNotificationRecipients(id)),
        dispatch(fetchShipmentDeliveryCargoFiles(id, true))
      ]);
      setInit(true);
    };

    if (isCopiedShipment) {
      dispatch(setShipmentData(copiedShipment));
      setInit(true);
    } else if (id) {
      fetchDataForEdit();
    } else {
      fetchDataForAdd();
    }

    dispatch(fetchAddressbooks());

    return () => {
      dispatch({ type: CLEAR_SHIPMENT });
      dispatch(clearProjectAlerts());
      dispatch(resetFormData());
      dispatch(clearAddressbooks());
      dispatch(clearProjectCustomFields());
      dispatch(clearCopiedShipment());
    };
  }, [copiedShipment, dispatch, id, isCopiedShipment]);

  const stepSubmitRef = useRef(null);
  const registerStepSubmit = useCallback(submitFn => {
    stepSubmitRef.current = submitFn;
  }, []);

  const handleStepClick = index => () => {
    // submit the current step first so its values reach shipmentForm
    if (stepSubmitRef.current) {
      dispatch(setAfterSubmitAction(() => {
        dispatch(goToStep(index));
      }));
      stepSubmitRef.current();
    } else {
      dispatch(goToStep(index));
    }
    dispatch(setJustSelectedTab(true));

    setTimeout(() => {
      dispatch(setJustSelectedTab(false));
    }, 2000);
  };

  const resetProducts = () => {
  };

  const getStepCompleted = form => {
    const completed = pristine[form] ?? true;
    return id && completed ? completed : undefined;
  };

  const formProps = {
    dispatch,
    SaveButton,
    CancelButton: CancelButtonComponent,
    registerStepSubmit,
    isAnyChanged,
    editable,
    formStatus,
    isCopiedShipment,
    isCreateMode: !id
  };

  if (!onInit) {
    return <PreloaderComponent />;
  }

  return (
    <Cnt>
      <StyledStepper
        alternativeLabel
        nonLinear={!!id}
        activeStep={step}
      >
        {steps.map(({ label, mobileLabel, form }, index) => (
          <StyledStep key={label} completed={getStepCompleted(form)}>
            <StepButton disabled={!id || index === step} onClick={handleStepClick(index)}>
              <StepLabel StepIconComponent={id ? CustomStepIcon : StepIcon}>
                <span style={{ fontWeight: index === step ? 500 : 400 }}>
                  {isMobile ? mobileLabel : label}
                </span>
              </StepLabel>
            </StepButton>
          </StyledStep>
        ))}
      </StyledStepper>
      {step === 0 && (
        <EditShipment
          {...props}
          {...formProps}
          form="EditShipmentForm"
          resetProducts={resetProducts}
        />
      )}
      {step === 1 && (<Delivery {...props} {...formProps} form="ShipmentDeliveryForm" />)}
      {step === 2 && (<Cargo {...formProps} form="ShipmentCargoForm" />)}
      {step === 3 && (<Notification {...formProps} form="ShipmentNotificationForm" />)}
      {step === 4 && (<Documents {...formProps} form="ShipmentDocumentsForm" />)}
    </Cnt>
  );
};

export default Shipment;
